/**
 * B2BClientFeedback — Carte « Retour du client » (Phase A++).
 *
 * Affichée lorsque la clinique a soit demandé une révision via
 * `cotation_modification_request`, soit refusé la cotation via
 * `cotation_rejection`. Présente les motifs structurés, le commentaire
 * libre, les lignes additionnelles demandées et les actions
 * ligne-par-ligne sous forme de badges colorés.
 */
import React from 'react';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import { MessageSquareWarning, Plus } from 'lucide-react';
import {
  fmtDateTime,
  REVISION_REASON_LABELS,
  REFUSAL_REASON_LABELS,
  LINE_ACTION_LABELS,
  LINE_ACTION_TONES,
} from './B2BConstants';

export const ClientFeedbackCard = ({ order }) => {
  const modif = order?.cotation_modification_request;
  const reject = order?.cotation_rejection;
  if (!modif && !reject) return null;

  const isRevision = !!modif;
  const data = isRevision ? modif : reject;
  const reasonLabels = isRevision ? REVISION_REASON_LABELS : REFUSAL_REASON_LABELS;
  const tone = isRevision
    ? 'border-amber-300 bg-amber-50/40'
    : (reject?.is_final ? 'border-red-300 bg-red-50/40' : 'border-rose-300 bg-rose-50/40');
  const titleTone = isRevision ? 'text-amber-900' : 'text-red-900';
  const title = isRevision
    ? 'Demande de révision (client)'
    : (reject?.is_final ? 'Refus définitif (client)' : 'Cotation rejetée (client)');

  return (
    <Card className={`border-2 ${tone}`} data-testid="b2b-client-feedback">
      <CardContent className="p-5 space-y-3">
        <h3 className={`text-sm font-bold flex items-center gap-1.5 ${titleTone}`}>
          <MessageSquareWarning className="w-4 h-4" /> {title}
        </h3>
        <div className="text-xs text-zinc-600 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Par <strong className="text-zinc-800">{data.by_name || '—'}</strong>
            {data.by_role ? <span className="text-zinc-500"> ({data.by_role})</span> : null}
          </span>
          <span>· {fmtDateTime(data.at)}</span>
        </div>

        {Array.isArray(data.reasons) && data.reasons.length > 0 && (
          <div>
            <p className="text-[11px] uppercase text-zinc-500 mb-1">Motifs sélectionnés</p>
            <div className="flex flex-wrap gap-1.5" data-testid="b2b-feedback-reasons">
              {data.reasons.map((r) => (
                <Badge key={r} variant="outline" className="bg-white text-zinc-800 border-zinc-300 text-[11px]">
                  {reasonLabels[r] || r}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {data.comment && (
          <div>
            <p className="text-[11px] uppercase text-zinc-500 mb-1">Commentaire libre</p>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap bg-white border border-zinc-200 rounded p-2" data-testid="b2b-feedback-comment">
              « {data.comment} »
            </p>
          </div>
        )}

        {isRevision && Array.isArray(modif.requested_items) && modif.requested_items.length > 0 && (
          <div>
            <p className="text-[11px] uppercase text-zinc-500 mb-1">
              Produits supplémentaires demandés ({modif.requested_items.length})
            </p>
            <ul className="text-sm bg-white border border-zinc-200 rounded divide-y divide-zinc-100" data-testid="b2b-feedback-requested-items">
              {modif.requested_items.map((it, i) => (
                <li key={i} className="px-2 py-1.5 flex items-start gap-2">
                  <Plus className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-zinc-800">{it.designation}</p>
                    <p className="text-xs text-zinc-500">
                      {it.quantity ? `Qté : ${it.quantity}` : 'Quantité non précisée'}
                      {it.observation ? ` · ${it.observation}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isRevision && Array.isArray(modif.line_actions) && modif.line_actions.length > 0 && (
          <div>
            <p className="text-[11px] uppercase text-zinc-500 mb-1">
              Actions sur lignes existantes ({modif.line_actions.length})
            </p>
            <ul className="text-sm bg-white border border-zinc-200 rounded divide-y divide-zinc-100" data-testid="b2b-feedback-line-actions">
              {modif.line_actions.map((la, i) => {
                const item = order.items?.[la.item_index];
                return (
                  <li key={i} className="px-2 py-1.5 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${LINE_ACTION_TONES[la.action] || ''}`}>
                        {LINE_ACTION_LABELS[la.action] || la.action}
                      </Badge>
                      <span className="font-medium text-zinc-800">
                        Ligne #{la.item_index + 1}
                        {item ? ` · ${item.designation}` : ''}
                      </span>
                      {la.requested_quantity != null && (
                        <span className="text-xs text-zinc-600">→ Qté demandée : <strong>{la.requested_quantity}</strong></span>
                      )}
                    </div>
                    {la.suggested_designation && (
                      <p className="text-xs text-zinc-600 pl-1">
                        Équivalent suggéré : <em>{la.suggested_designation}</em>
                      </p>
                    )}
                    {la.comment && (
                      <p className="text-xs text-zinc-700 pl-1 italic">« {la.comment} »</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!isRevision && reject?.is_final === false && (
          <p className="text-[11px] text-zinc-500 italic">
            Refus non définitif (mode legacy) — le client peut être recontacté pour une nouvelle proposition.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
